import { useState } from 'react';
import { Button } from '@/components/ui/PageHeader';
import { InputField } from '@/components/ui/FormFields';
import { prepareProviderMakeGood, type ProviderMakeGoodResult } from '@/api/providers';

export function ProviderMakeGoodPanel() {
  const [campaignId, setCampaignId] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [result, setResult] = useState<ProviderMakeGoodResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function prepare(apply: boolean) {
    setBusy(true);
    setError('');
    try {
      const response = await prepareProviderMakeGood(campaignId.trim(), {
        startsAt: new Date(startsAt).toISOString(), expiresAt: new Date(expiresAt).toISOString(),
        apply, ...(apply && result ? { expectedRevision: result.expectedRevision } : {}),
      });
      setResult(response.data);
    } catch (cause) {
      setResult(null);
      setError(cause instanceof Error ? cause.message : 'The replacement could not be prepared.');
    } finally {
      setBusy(false);
    }
  }

  return <section className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
    <h2 className="font-semibold">Free campaign replacement</h2>
    <p className="text-sm text-gray-600">Admin only. Prepare one replacement draft for a completed free campaign that missed its internal delivery target. Nothing is charged or published. The Provider must review the dates and terms, submit it for review, and publish after approval.</p>
    <InputField label="Original campaign ID" aria-label="Original campaign ID" value={campaignId} disabled={busy}
      onChange={event => { setCampaignId(event.target.value); setResult(null); }} />
    <InputField label="Replacement start (your local time)" aria-label="Replacement start" type="datetime-local" value={startsAt} disabled={busy}
      onChange={event => { setStartsAt(event.target.value); setResult(null); }} />
    <InputField label="Replacement end (up to seven days later)" aria-label="Replacement end" type="datetime-local" value={expiresAt} disabled={busy}
      onChange={event => { setExpiresAt(event.target.value); setResult(null); }} />
    <Button disabled={busy || !campaignId.trim() || !startsAt || !expiresAt} onClick={() => prepare(false)}>Preview replacement</Button>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {result && <div aria-live="polite" className="text-sm space-y-2">
      <p>{result.providerName} — {result.sourceHeadline}</p>
      <p>{result.eligibleRenders} eligible renders against an internal target of {result.targetEligibleRenders}. No footfall or sales guarantee.</p>
      <p>{result.startsAt} to {result.expiresAt} (UTC)</p>
      {result.status === 'preview' && <Button disabled={busy} onClick={() => prepare(true)}>Create replacement draft</Button>}
      {result.replacementCampaignId && <p>Draft created: <code>{result.replacementCampaignId}</code>. The original report and dates have not changed. Continue through the existing Provider campaign workflow.</p>}
    </div>}
  </section>;
}
